import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import { Avatar, Button, Dropdown, Modal, Popconfirm, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { EmptyState } from "@/shared/components/EmptyState";
import { StatusTag } from "@/shared/components/StatusTag";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { resolveImageUrl } from "@/shared/utils/resolveImageUrl";
import { tablePagination } from "@/shared/utils/tablePagination";
import { useDeleteStaff, useStaffList } from "../hooks/useStaff";
import { useStaffStore } from "../store/staff.store";
import type { Staff, StaffType } from "../types";
import { formatWorkdaysDisplay } from "../utils/formatWorkdays";

const STAFF_TYPE_LABELS: Record<StaffType, string> = {
	EMPLOYEE: "Xodim",
	BRIGADIER: "Brigadir",
	SECURITY: "Xavfsizlik",
	ADMIN: "Admin",
	MANAGER: "Menejer",
};

interface StaffTableProps {
	organizationId: string;
	organizationName?: string;
	brigadeId?: string;
	page: number;
	limit: number;
	onPageChange: (page: number) => void;
	/** When true, row click and name click navigate to staff detail */
	enableRowNavigate?: boolean;
}

export function StaffTable({
	organizationId,
	organizationName,
	brigadeId,
	page,
	limit,
	onPageChange,
	enableRowNavigate = true,
}: StaffTableProps) {
	const navigate = useNavigate();
	const { data, isLoading } = useStaffList({ organizationId, brigadeId, page, limit });
	const { mutate: remove } = useDeleteStaff();
	const openEditModal = useStaffStore((s) => s.openEditModal);
	const [previewImage, setPreviewImage] = useState<string | null>(null);

	const handleRowClick = (record: Staff) => {
		if (enableRowNavigate) {
			navigate(`/staff/${record.id}`);
		}
	};

	const columns: ColumnsType<Staff> = [
		{
			title: "№",
			key: "index",
			width: 55,
			align: "center",
			render: (_: unknown, __: unknown, index: number) => formatTableIndex(page, limit, index),
		},
		...(organizationName
			? [
					{
						title: "Tashkilot",
						key: "organization",
						width: 140,
						render: () => organizationName,
					},
				]
			: []),
		{
			title: "Xodim",
			key: "staff",
			render: (_: unknown, record: Staff) => {
				const photoUrl =
					(record as Staff & { photo?: { url?: string } }).photo?.url ?? record.photoUrl;
				const resolvedUrl = resolveImageUrl(photoUrl);
				return (
					<div className="flex items-center gap-2">
						{resolvedUrl ? (
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									setPreviewImage(resolvedUrl);
								}}
								className="p-0 border-none bg-transparent cursor-pointer"
								aria-label={`${record.fullname} rasmini ko'rish`}
							>
								<Avatar
									size={32}
									style={{ background: "#1D2026", color: "#fff", fontSize: 13 }}
									src={resolvedUrl}
								>
									{record.fullname?.charAt(0)?.toUpperCase() ?? "X"}
								</Avatar>
							</button>
						) : (
							<Avatar size={32} style={{ background: "#1D2026", color: "#fff", fontSize: 13 }}>
								{record.fullname?.charAt(0)?.toUpperCase() ?? "X"}
							</Avatar>
						)}
						<div>
							{enableRowNavigate ? (
								<button
									type="button"
									className="font-medium text-sm cursor-pointer hover:text-info hover:underline p-0 border-none bg-transparent text-left"
									onClick={(e) => {
										e.stopPropagation();
										navigate(`/staff/${record.id}`);
									}}
								>
									{record.fullname}
								</button>
							) : (
								<div className="font-medium text-sm">{record.fullname}</div>
							)}
							{record.phoneNumber && (
								<div className="text-xs text-ink-faint font-mono">{record.phoneNumber}</div>
							)}
						</div>
					</div>
				);
			},
		},
		{
			title: "Xodim turi",
			key: "staffType",
			width: 100,
			render: (_: unknown, record: Staff) => {
				const type = record.staffType ?? "EMPLOYEE";
				return STAFF_TYPE_LABELS[type] ?? type;
			},
		},
		// Brigade column is redundant when the table is already scoped to one brigade
		...(brigadeId
			? []
			: [
					{
						title: "Brigada",
						key: "brigade",
						width: 130,
						render: (_: unknown, record: Staff) =>
							record.brigade?.name ? <Tag color="blue">{record.brigade.name}</Tag> : "—",
					},
				]),
		{
			title: "Ishga kirgan sana",
			dataIndex: "jobEntryDate",
			key: "jobEntryDate",
			width: 130,
			render: (date: string | null) =>
				date
					? new Date(date).toLocaleDateString("uz-UZ", {
							year: "numeric",
							month: "2-digit",
							day: "2-digit",
						})
					: "—",
		},
		{
			title: "Ish soatlari",
			key: "hours",
			width: 130,
			render: (_: unknown, record: Staff) => (
				<div className="text-sm leading-tight">
					<div>
						{record.maxHoursPerWeek} <span className="text-ink-soft text-xs">soat/hafta</span>
					</div>
					<div>
						{record.maxHoursPerDay} <span className="text-ink-soft text-xs">soat/kun</span>
					</div>
				</div>
			),
		},
		...(brigadeId
			? [
					{
						title: "Ish vaqti",
						key: "workTime",
						width: 180,
						render: (_: unknown, record: Staff) => (
							<span className="text-sm text-ink-soft">
								{formatWorkdaysDisplay(record.workdays)}
							</span>
						),
					},
				]
			: []),
		{
			title: "Holat",
			dataIndex: "isActive",
			key: "isActive",
			width: 90,
			render: (isActive: boolean) =>
				isActive ? (
					<StatusTag tone="success">Faol</StatusTag>
				) : (
					<StatusTag tone="default">Nofaol</StatusTag>
				),
		},
		{
			title: "Amallar",
			key: "actions",
			width: 80,
			align: "center",
			render: (_: unknown, record: Staff) => (
				// biome-ignore lint/a11y/noStaticElementInteractions: stop propagation only, not a focusable control
				<span
					className="inline-block"
					onClick={(e) => e.stopPropagation()}
					onKeyDown={(e) => e.stopPropagation()}
					role="presentation"
				>
					<Dropdown
						menu={{
							items: [
								{
									key: "edit",
									icon: <EditOutlined />,
									label: "Tahrirlash",
									onClick: () => openEditModal(record),
								},
								{
									key: "delete",
									danger: true,
									label: (
										// biome-ignore lint/a11y/useKeyWithClickEvents: ignore
										// biome-ignore lint/a11y/noStaticElementInteractions: ignore
										<div onClick={(e) => e.stopPropagation()}>
											<Popconfirm
												title="O'chirish"
												description="Rostdan ham o'chirmoqchimisiz?"
												onConfirm={() => remove(record.id)}
												okText="Ha"
												cancelText="Yo'q"
												okButtonProps={{ danger: true }}
											>
												<div
													style={{
														width: "100%",
														display: "flex",
														alignItems: "center",
														gap: "8px",
													}}
												>
													<DeleteOutlined />
													<span>O'chirish</span>
												</div>
											</Popconfirm>
										</div>
									),
								},
							],
						}}
						trigger={["click"]}
						placement="bottomRight"
					>
						<Button type="text" icon={<MoreOutlined />} size="small" />
					</Dropdown>
				</span>
			),
		},
	];

	return (
		<>
			<Table
				onRow={
					enableRowNavigate
						? (record) => ({
								onClick: () => handleRowClick(record),
								className: "cursor-pointer",
							})
						: undefined
				}
				title={
					organizationName
						? () => (
								<div className="flex items-center gap-2">
									<span className="text-ink-soft">Tashkilot:</span>
									<Tag>{organizationName}</Tag>
								</div>
							)
						: undefined
				}
				columns={columns}
				dataSource={data?.data?.items}
				loading={isLoading}
				rowKey="id"
				pagination={tablePagination({
					page,
					limit,
					total: data?.data?.meta?.total,
					onChange: onPageChange,
					sizeChanger: false,
				})}
				size="middle"
				locale={{
					emptyText: (
						<EmptyState
							description={
								organizationName ? (
									<span>
										Xodimlar yo'q — <strong>{organizationName}</strong>
									</span>
								) : (
									"Ma'lumot yo'q"
								)
							}
						/>
					),
				}}
			/>
			<Modal
				open={Boolean(previewImage)}
				onCancel={() => setPreviewImage(null)}
				footer={null}
				width={720}
				destroyOnHidden
			>
				{previewImage ? (
					<img
						src={previewImage}
						alt="Xodim rasmi"
						style={{ width: "100%", maxHeight: "72vh", objectFit: "contain", borderRadius: 8 }}
					/>
				) : null}
			</Modal>
		</>
	);
}
